import { Box, Stack } from "@repo/ui/core";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";

const DEVFILES: {
  persona: string;
  color: string;
  code: string;
}[] = [
  {
    persona: "Platform Developer",
    color: "#4ade80",
    code: `schemaVersion: 2.2.0
variables:
  image-tag: main
  registry: registry.gitlab.com/null-autos

components:
  - name: platform-ide
    attributes:
      gl/inject-editor: true
    container:
      image: "{{registry}}/workspaces:{{image-tag}}"
      memoryRequest: 8Gi
      memoryLimit: 32Gi
      cpuRequest: 4
      cpuLimit: 16
      env:
        - name: AOSP_ROOT
          value: /workspace/aosp
        - name: LUNCH_TARGET
          value: cf_x86_64_auto-userdebug
      endpoints:
        - name: adb-bridge
          targetPort: 5555
          exposure: internal
  - name: aosp-cache
    volume:
      size: 200Gi`,
  },
  {
    persona: "Application Developer",
    color: "#22d3ee",
    code: `schemaVersion: 2.2.0
variables:
  image-tag: main
  registry: registry.gitlab.com/null-autos

components:
  - name: app-ide
    attributes:
      gl/inject-editor: true
    container:
      image: "{{registry}}/workspaces:{{image-tag}}"
      memoryRequest: 4Gi
      memoryLimit: 16Gi
      cpuRequest: 2
      cpuLimit: 8
      env:
        - name: ANDROID_HOME
          value: /opt/android-sdk
        - name: GRADLE_OPTS
          value: -Xmx4g -XX:+UseG1GC
      endpoints:
        - name: studio-preview
          targetPort: 8080
          exposure: public
        - name: adb
          targetPort: 5037
          exposure: internal
  - name: gradle-cache
    volume:
      size: 50Gi`,
  },
  {
    persona: "Mobile Developer",
    color: "#a78bfa",
    code: `schemaVersion: 2.2.0
variables:
  image-tag: main
  registry: registry.gitlab.com/null-autos

components:
  - name: mobile-ide
    attributes:
      gl/inject-editor: true
    container:
      image: "{{registry}}/workspaces:{{image-tag}}"
      memoryRequest: 2Gi
      memoryLimit: 8Gi
      cpuRequest: 1
      cpuLimit: 4
      env:
        - name: FLUTTER_ROOT
          value: /opt/flutter
        - name: CHROME_EXECUTABLE
          value: /usr/bin/chromium
      endpoints:
        - name: hot-reload
          targetPort: 8100
          exposure: public
        - name: devtools
          targetPort: 9100
          exposure: public
  - name: packages-cache
    volume:
      size: 20Gi`,
  },
];

const LINE_H = 14.5;
const PAD_X = 14;
const PAD_Y = 12;
const CARD_GAP = 16;

function syntaxColor(line: string): string {
  const trimmed = line.trimStart();
  if (trimmed.startsWith("#")) return "rgba(255,255,255,0.25)";
  if (trimmed.startsWith("- name:") || trimmed.startsWith("- name :"))
    return "#4ade80";
  if (/^\w[\w-]*:/.test(trimmed)) return "#60a5fa";
  if (trimmed.startsWith("- ")) return "#fb923c";
  if (/value:/.test(trimmed)) return "#fbbf24";
  if (/image:/.test(trimmed)) return "#a78bfa";
  if (/"?\{\{/.test(trimmed)) return "#f472b6";
  return "rgba(255,255,255,0.7)";
}

export function CloudWorkstationsSlide() {
  const maxLines = Math.max(...DEVFILES.map((d) => d.code.split("\n").length));
  const codeH = maxLines * LINE_H + PAD_Y * 2;
  const headerH = 36;
  const cardW = 340;
  const totalW = cardW * 3 + CARD_GAP * 2;
  const svgH = headerH + codeH + 8;

  return (
    <Box w="100%" maw={1400} px="xl" h="100%">
      <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
        <SlideTitle
          title="Cloud Workstations"
          subtitle="Devfile-powered environments tailored to every developer persona"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          style={{ flex: 1, minHeight: 0 }}
        >
          <svg
            viewBox={`0 0 ${totalW} ${svgH}`}
            width="100%"
            style={{ display: "block", maxHeight: "68vh" }}
          >
            {DEVFILES.map((df, di) => {
              const x = di * (cardW + CARD_GAP);
              const lines = df.code.split("\n");

              return (
                <motion.g
                  key={df.persona}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + di * 0.15, duration: 0.4 }}
                >
                  {/* Card background */}
                  <rect
                    x={x}
                    y={0}
                    width={cardW}
                    height={svgH}
                    rx={10}
                    fill="rgba(255,255,255,0.025)"
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth={1}
                  />

                  {/* Header */}
                  <rect
                    x={x}
                    y={0}
                    width={cardW}
                    height={headerH}
                    rx={10}
                    fill="rgba(255,255,255,0.03)"
                  />
                  <rect
                    x={x}
                    y={headerH - 1}
                    width={cardW}
                    height={2}
                    fill="rgba(255,255,255,0.06)"
                  />
                  {/* Accent dot */}
                  <circle cx={x + 16} cy={headerH / 2} r={5} fill={df.color} opacity={0.8} />
                  <text
                    x={x + 30}
                    y={headerH / 2 + 4.5}
                    fill={df.color}
                    fontSize={12}
                    fontWeight={700}
                    fontFamily="inherit"
                    letterSpacing="0.02em"
                  >
                    {df.persona}
                  </text>

                  {/* Code lines */}
                  {lines.map((line, li) => {
                    const indent = line.length - line.trimStart().length;
                    const CHAR_W = 5.7;
                    return (
                      <text
                        key={li}
                        x={x + PAD_X + indent * CHAR_W}
                        y={headerH + PAD_Y + li * LINE_H + 10}
                        fill={syntaxColor(line)}
                        fontSize={9.5}
                        fontFamily="'Geist Mono', monospace"
                        fontWeight={400}
                      >
                        {line.trimStart()}
                      </text>
                    );
                  })}
                </motion.g>
              );
            })}
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
